import React,{useState,useEffect,useRef,memo} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMap,FiLayers,FiRefreshCw,FiDownload,FiExternalLink,FiZoomIn,FiZoomOut,FiMaximize,FiMapPin,FiAlertCircle}=FiIcons;

const TyphoonMap=memo(({typhoonData,isVisible=true})=> {
  const [selectedLayer,setSelectedLayer]=useState('windy-iligan');
  const [mapLoading,setMapLoading]=useState(false);
  const [lastUpdated,setLastUpdated]=useState(null);
  const [downloadStatus,setDownloadStatus]=useState({});
  const mapContainerRef=useRef(null);

  // Iligan City coordinates: 8.2280° N,124.2452° E
  const ILIGAN_COORDS={lat: 8.2280,lon: 124.2452};

  // Map sources centered on Iligan City and Northern Mindanao
  const mapSources=[
    {
      id: 'windy-iligan',
      name: 'Windy - Iligan Focus',
      description: 'Interactive typhoon tracking centered on Iligan City with wind patterns',
      url: `https://embed.windy.com/embed2.html?lat=${ILIGAN_COORDS.lat}&lon=${ILIGAN_COORDS.lon}&detailLat=${ILIGAN_COORDS.lat}&detailLon=${ILIGAN_COORDS.lon}&width=650&height=450&zoom=8&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=km%2Fh&metricTemp=%C2%B0C&radarRange=-1`,
      type: 'iframe',
      category: 'interactive',
      focus: 'Iligan City & Mindanao Sea approaches'
    },
    {
      id: 'windy-mindanao',
      name: 'Windy - Mindanao Regional',
      description: 'Broader view of Mindanao and surrounding seas',
      url: `https://embed.windy.com/embed2.html?lat=7.5&lon=125&detailLat=${ILIGAN_COORDS.lat}&detailLon=${ILIGAN_COORDS.lon}&width=650&height=450&zoom=6&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=km%2Fh&metricTemp=%C2%B0C&radarRange=-1`,
      type: 'iframe',
      category: 'interactive',
      focus: 'Mindanao Island & Philippine Sea'
    },
    {
      id: 'windy-philippines',
      name: 'Windy - Philippines Wide',
      description: 'Full Philippines view for typhoon approach tracking',
      url: `https://embed.windy.com/embed2.html?lat=12&lon=122&detailLat=${ILIGAN_COORDS.lat}&detailLon=${ILIGAN_COORDS.lon}&width=650&height=450&zoom=5&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=km%2Fh&metricTemp=%C2%B0C&radarRange=-1`,
      type: 'iframe',
      category: 'interactive',
      focus: 'Philippine Archipelago & Typhoon Alley'
    },
    {
      id: 'nullschool-mindanao',
      name: 'Earth - Mindanao Focus',
      description: 'Global wind and weather conditions centered on Northern Mindanao',
      url: `https://earth.nullschool.net/#current/wind/surface/level/orthographic=${ILIGAN_COORDS.lon},${ILIGAN_COORDS.lat},1024/loc=${ILIGAN_COORDS.lon},${ILIGAN_COORDS.lat}`,
      type: 'iframe',
      category: 'interactive',
      focus: 'Iligan Bay & Bohol Sea'
    },
    {
      id: 'zoom-earth-iligan',
      name: 'Zoom Earth - Iligan Region',
      description: 'Real-time satellite imagery focused on Iligan and surroundings',
      // FIXED: Updated Zoom Earth URL structure
      url: `https://zoom.earth/maps/satellite/#view=${ILIGAN_COORDS.lat},${ILIGAN_COORDS.lon},9z`,
      type: 'iframe',
      category: 'satellite',
      focus: 'Northern Mindanao coastline'
    }
  ];

  // Updated downloadable map resources with working alternatives
  const downloadableResources=[
    {
      name: 'PAGASA Weather Map Portal',
      url: 'https://www.pagasa.dost.gov.ph/weather/weather-forecast-and-advisories',
      description: 'Official Philippine weather maps and radar imagery',
      format: 'Interactive',
      updateFreq: 'Real-time',
      coverage: 'Philippines with Mindanao focus',
      fallbackUrl: 'https://www.pagasa.dost.gov.ph/',
      type: 'portal'
    },
    {
      name: 'Windy Philippines View',
      url: `https://www.windy.com/${ILIGAN_COORDS.lat}/${ILIGAN_COORDS.lon}?${ILIGAN_COORDS.lat},${ILIGAN_COORDS.lon},8`,
      description: 'Interactive weather map centered on Iligan City',
      format: 'Interactive',
      updateFreq: '3 hours',
      coverage: 'Northern Mindanao detailed view',
      fallbackUrl: 'https://www.windy.com',
      type: 'interactive'
    },
    {
      name: 'JMA Himawari Real-time',
      url: 'https://www.jma.go.jp/bosai/map.html#5/125.5/10.0/&elem=wind&contents=himawari',
      description: 'Japanese satellite imagery covering Philippine waters',
      format: 'Interactive',
      updateFreq: '10 minutes',
      coverage: 'Western Pacific & Philippines',
      fallbackUrl: 'https://www.jma.go.jp/jma/en/forecast/',
      type: 'satellite'
    },
    {
      name: 'Earth Nullschool - Mindanao',
      url: `https://earth.nullschool.net/#current/wind/surface/level/orthographic=${ILIGAN_COORDS.lon},${ILIGAN_COORDS.lat},1024`,
      description: 'Global weather visualization focused on Mindanao region',
      format: 'Interactive',
      updateFreq: 'Real-time',
      coverage: 'Global with Mindanao focus',
      fallbackUrl: 'https://earth.nullschool.net/',
      type: 'interactive'
    },
    {
      name: 'NOAA Weather',
      url: 'https://www.weather.gov/phx/',
      description: 'NOAA weather conditions and forecasts',
      format: 'Interactive',
      updateFreq: '1 hour',
      coverage: 'Global meteorological data',
      fallbackUrl: 'https://www.weather.gov/',
      type: 'interactive'
    },
    {
      name: 'USGS Earthquake Monitoring',
      url: 'https://earthquake.usgs.gov/earthquakes/map/',
      description: 'Global earthquake monitoring and alerts',
      format: 'Interactive',
      updateFreq: 'Real-time',
      coverage: 'Worldwide seismic activity',
      fallbackUrl: 'https://earthquake.usgs.gov/',
      type: 'interactive'
    }
  ];

  const currentMap=mapSources.find(source=> source.id===selectedLayer);

  // Handle map layer change
  const handleLayerChange=(layerId)=> {
    setMapLoading(true);
    setSelectedLayer(layerId);
    setTimeout(()=> {
      setMapLoading(false);
      setLastUpdated(new Date());
    },1000);
  };

  // Handle map refresh
  const handleRefresh=()=> {
    setMapLoading(true);
    // Force refresh by updating the iframe src
    const iframe=mapContainerRef.current?.querySelector('iframe');
    const img=mapContainerRef.current?.querySelector('img');
    
    if (iframe) {
      const originalSrc=iframe.src;
      iframe.src='';
      setTimeout(()=> {
        iframe.src=originalSrc + (originalSrc.includes('?') ? '&' : '?') + 't=' + Date.now();
        setMapLoading(false);
        setLastUpdated(new Date());
      },500);
    } else if (img) {
      const originalSrc=img.src;
      img.src=originalSrc + (originalSrc.includes('?') ? '&' : '?') + 't=' + Date.now();
      img.onload=()=> {
        setMapLoading(false);
        setLastUpdated(new Date());
      };
      img.onerror=()=> {
        setMapLoading(false);
        console.error('Failed to load map image:',originalSrc);
      };
    } else {
      setTimeout(()=> {
        setMapLoading(false);
        setLastUpdated(new Date());
      },1000);
    }
  };

  // Enhanced download handler with better error handling
  const handleDownload=async (resource)=> {
    const resourceId=resource.name.replace(/\s+/g,'-').toLowerCase();
    try {
      setDownloadStatus(prev=> ({...prev,[resourceId]: 'loading'}));
      
      // Different handling based on resource type
      if (resource.type==='direct') {
        // For direct image URLs,try to open in new tab
        window.open(resource.url,'_blank','noopener,noreferrer');
        setDownloadStatus(prev=> ({...prev,[resourceId]: 'success'}));
      } else if (resource.type==='portal' || resource.type==='interactive' || resource.type==='satellite') {
        // For interactive resources,open directly
        window.open(resource.url,'_blank','noopener,noreferrer');
        setDownloadStatus(prev=> ({...prev,[resourceId]: 'success'}));
      } else {
        // Fallback: try to fetch and download
        try {
          const response=await fetch(resource.url,{
            mode: 'no-cors',
            method: 'GET'
          });
          
          // Since we can't read the response in no-cors mode,
          // just open in new tab as fallback
          window.open(resource.url,'_blank','noopener,noreferrer');
          setDownloadStatus(prev=> ({...prev,[resourceId]: 'success'}));
        } catch (fetchError) {
          console.warn('Direct fetch failed,using fallback URL:',fetchError);
          window.open(resource.fallbackUrl || resource.url,'_blank','noopener,noreferrer');
          setDownloadStatus(prev=> ({...prev,[resourceId]: 'fallback'}));
        }
      }
      
      // Reset status after 3 seconds
      setTimeout(()=> {
        setDownloadStatus(prev=> ({...prev,[resourceId]: null}));
      },3000);
    } catch (error) {
      console.error('Download failed:',error);
      setDownloadStatus(prev=> ({...prev,[resourceId]: 'error'}));
      
      // Try fallback URL if available
      if (resource.fallbackUrl) {
        setTimeout(()=> {
          window.open(resource.fallbackUrl,'_blank','noopener,noreferrer');
          setDownloadStatus(prev=> ({...prev,[resourceId]: 'fallback'}));
        },1000);
      }
      
      // Reset error status after 5 seconds
      setTimeout(()=> {
        setDownloadStatus(prev=> ({...prev,[resourceId]: null}));
      },5000);
    }
  };

  // Get download button state
  const getDownloadButtonState=(resource)=> {
    const resourceId=resource.name.replace(/\s+/g,'-').toLowerCase();
    const status=downloadStatus[resourceId];
    
    switch (status) {
      case 'loading':
        return {icon: FiRefreshCw,className: 'bg-blue-600 animate-spin',disabled: true,title: 'Opening...'};
      case 'success':
        return {icon: FiDownload,className: 'bg-green-600',disabled: false,title: 'Opened Successfully'};
      case 'error':
        return {icon: FiAlertCircle,className: 'bg-red-600',disabled: false,title: 'Failed - Click to Retry'};
      case 'fallback':
        return {icon: FiExternalLink,className: 'bg-orange-600',disabled: false,title: 'Opened Fallback Source'};
      default:
        return {icon: FiExternalLink,className: 'bg-blue-600 hover:bg-blue-700',disabled: false,title: 'Open/Download'};
    }
  };

  // Open map in full screen
  const openFullscreen=()=> {
    if (currentMap) {
      window.open(currentMap.url,'_blank','width=1200,height=800');
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div 
      initial={{opacity: 0,y: 20}} 
      animate={{opacity: 1,y: 0}} 
      transition={{delay: 0.2}}
      className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <SafeIcon icon={FiMap} className="text-xl text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <SafeIcon icon={FiMapPin} className="mr-2 text-green-600" />
                Iligan-Centered Typhoon Tracking
              </h2>
              <p className="text-sm text-gray-600">
                📍 Centered on Iligan City (8.228°N,124.245°E) • Real-time satellite & weather radar
              </p>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleRefresh}
              disabled={mapLoading}
              className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
              title="Refresh Map"
            >
              <SafeIcon icon={FiRefreshCw} className={`text-sm ${mapLoading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={openFullscreen}
              className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              title="Open Fullscreen"
            >
              <SafeIcon icon={FiMaximize} className="text-sm" />
            </button>
          </div>
        </div>
        
        {/* Layer Selection */}
        <div className="mt-3 flex flex-wrap gap-2">
          {mapSources.map((source)=> (
            <button
              key={source.id}
              onClick={()=> handleLayerChange(source.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedLayer===source.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              {source.name}
              {/* Add focus indicator for Iligan-specific views */}
              {source.focus?.includes('Iligan') && (
                <span className="ml-1 text-xs">📍</span>
              )}
            </button>
          ))}
        </div>
        
        {/* Current map focus info */}
        {currentMap && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
            <strong>Focus Area:</strong> {currentMap.focus} • <strong>Category:</strong> {currentMap.category} • <strong>Coverage:</strong> {currentMap.description}
          </div>
        )}
      </div>
      
      {/* Map Container */}
      <div className="relative">
        {mapLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="text-center">
              <SafeIcon icon={FiRefreshCw} className="text-3xl text-blue-600 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Loading Iligan-centered map data...</p>
            </div>
          </div>
        )}
        
        <div ref={mapContainerRef} className="w-full h-96 bg-gray-50">
          {currentMap?.type==='iframe' ? (
            <iframe 
              src={currentMap.url} 
              className="w-full h-full border-0" 
              title={currentMap.name}
              loading="lazy"
              onLoad={()=> {
                setMapLoading(false);
                setLastUpdated(new Date());
              }}
              onError={()=> {
                setMapLoading(false);
                console.error('Failed to load map iframe:',currentMap.url);
              }}
            />
          ) : currentMap?.type==='image' ? (
            <img 
              src={currentMap.url} 
              alt={currentMap.name} 
              className="w-full h-full object-cover"
              onLoad={()=> {
                setMapLoading(false);
                setLastUpdated(new Date());
              }}
              onError={()=> {
                setMapLoading(false);
                console.error('Failed to load map image:',currentMap.url);
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <SafeIcon icon={FiMap} className="text-4xl text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">Select a map layer above</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Map Info Overlay */}
        {currentMap && (
          <div className="absolute bottom-2 left-2 right-2">
            <div className="bg-black bg-opacity-75 text-white p-2 rounded text-xs">
              <div className="flex items-center justify-between">
                <div>
                  <strong>{currentMap.name}</strong>
                  <p className="text-gray-300">{currentMap.description}</p>
                  <p className="text-blue-300 mt-1">
                    🎯 Monitoring: {currentMap.focus}
                  </p>
                </div>
                {lastUpdated && (
                  <div className="text-right text-gray-300">
                    <p>Updated:</p>
                    <p>{lastUpdated.toLocaleTimeString('en-PH')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Enhanced Download Section */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <SafeIcon icon={FiDownload} className="mr-2 text-blue-600" />
          Mindanao Region Weather Resources
        </h3>
        
        {/* Important Notice */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <SafeIcon icon={FiAlertCircle} className="text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-xs text-blue-800">
              <strong>Notice:</strong> These resources open in new tabs for best experience. Some may require direct access due to security policies. All sources provide official weather data for Northern Mindanao region.
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {downloadableResources.map((resource,index)=> {
            const buttonState=getDownloadButtonState(resource);
            return (
              <motion.div
                key={index}
                initial={{opacity: 0,scale: 0.95}}
                animate={{opacity: 1,scale: 1}}
                transition={{delay: 0.1 * index}}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 truncate">
                      {resource.name}
                    </h4>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {resource.description}
                    </p>
                  </div>
                  <button
                    onClick={()=> handleDownload(resource)}
                    disabled={buttonState.disabled}
                    className={`ml-2 p-1.5 text-white rounded transition-colors ${buttonState.className}`}
                    title={buttonState.title}
                  >
                    <SafeIcon icon={buttonState.icon} className="text-xs" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-2">
                    <span className="bg-gray-100 px-2 py-0.5 rounded">
                      {resource.format}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      {resource.coverage}
                    </span>
                  </div>
                  <span>Updates: {resource.updateFreq}</span>
                </div>
                
                {/* Resource type indicator */}
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    {resource.type==='direct' && <span className="text-xs text-green-600">📊 Direct Access</span>}
                    {resource.type==='interactive' && <span className="text-xs text-blue-600">🌐 Interactive</span>}
                    {resource.type==='satellite' && <span className="text-xs text-purple-600">🛰️ Satellite</span>}
                    {resource.type==='portal' && <span className="text-xs text-orange-600">🏛️ Official Portal</span>}
                  </div>
                  {downloadStatus[resource.name.replace(/\s+/g,'-').toLowerCase()]==='success' && (
                    <span className="text-xs text-green-600">✓ Opened</span>
                  )}
                  {downloadStatus[resource.name.replace(/\s+/g,'-').toLowerCase()]==='error' && (
                    <span className="text-xs text-red-600">⚠️ Failed</span>
                  )}
                  {downloadStatus[resource.name.replace(/\s+/g,'-').toLowerCase()]==='fallback' && (
                    <span className="text-xs text-orange-600">↗️ Fallback</span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      
      {/* Typhoon-specific overlay */}
      {typhoonData && (
        <div className="p-4 bg-orange-50 border-t border-orange-200">
          <div className="flex items-center mb-2">
            <SafeIcon icon={FiLayers} className="mr-2 text-orange-600" />
            <h3 className="text-sm font-semibold text-orange-800">
              Active Typhoon: {typhoonData.localName} - Iligan Impact Assessment
            </h3>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-gray-600">Position:</p>
              <p className="font-medium text-gray-900">{typhoonData.position || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Winds:</p>
              <p className="font-medium text-gray-900">{typhoonData.maxWinds || 'N/A'} kph</p>
            </div>
            <div>
              <p className="text-gray-600">Movement:</p>
              <p className="font-medium text-gray-900">{typhoonData.movement || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Iligan Risk:</p>
              <p className="font-medium text-orange-900">
                {typhoonData.signals?.some(s=> s.areas.toLowerCase().includes('mindanao') || s.areas.toLowerCase().includes('lanao') || s.areas.toLowerCase().includes('misamis')) 
                  ? 'HIGH - Direct Impact' 
                  : 'MONITOR - Indirect Effects'}
              </p>
            </div>
          </div>
          
          {/* Iligan-specific impact assessment */}
          <div className="mt-3 p-2 bg-orange-100 border border-orange-300 rounded">
            <p className="text-orange-800 text-xs">
              <strong>Iligan City Impact:</strong> Monitor Mandulog River levels,coastal storm surge in Iligan Bay,and wind effects on highland barangays (Saray). Coordinate with Iligan DRRMO for evacuation protocols.
            </p>
          </div>
        </div>
      )}
      
      {/* Footer with enhanced attribution */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          <strong>Iligan Focus:</strong> All resources centered on Iligan City (8.228°N,124.245°E) for optimal local monitoring • <strong>Sources:</strong> PAGASA,Windy.com,JMA,Earth.nullschool.net,NOAA,USGS • <strong>Coverage:</strong> Mindanao Sea,Bohol Sea,and typhoon approach routes to Northern Mindanao • <strong>Access:</strong> Resources open in new tabs for security and performance
        </p>
      </div>
    </motion.div>
  );
});

TyphoonMap.displayName='TyphoonMap';

export default TyphoonMap;